import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Building, TrendingUp, Calendar, Plus, Mail, Phone, MapPin, Linkedin, Users, Target } from 'lucide-react';
import { Lead } from '@/types/lead';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLeadCreated: (lead: Lead) => void;
}

interface CreateLeadData {
  name: string;
  company: string;
  email?: string;
  phoneNumber?: string;
  industry?: string;
  companySize?: string;
  linkedinUrl?: string;
  location?: string;
  contactPerson?: string;
  priority: 'HOT' | 'WARM' | 'COLD';
  stage: 'NEW' | 'CONTACTED' | 'DISCUSSION' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST';
  dealValue?: number;
  expectedClose?: string;
  assignedTo?: string;
  sourceId?: string;
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Real Estate',
  'Consulting',
  'Marketing',
  'Legal',
  'Other'
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees'
];

const PIPELINE_STAGES = [
  { value: 'NEW', label: 'New', description: 'Initial contact' },
  { value: 'CONTACTED', label: 'Contacted', description: 'First contact made' },
  { value: 'DISCUSSION', label: 'Discussion', description: 'In discussions' },
  { value: 'PROPOSAL', label: 'Proposal', description: 'Proposal sent' },
  { value: 'NEGOTIATION', label: 'Negotiation', description: 'Negotiating terms' },
  { value: 'WON', label: 'Won', description: 'Deal closed' },
  { value: 'LOST', label: 'Lost', description: 'Deal lost' }
];

export const AddLeadModal: React.FC<AddLeadModalProps> = ({
  isOpen,
  onClose,
  onLeadCreated
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<CreateLeadData>({
    name: '',
    company: '',
    email: '',
    phoneNumber: '',
    industry: '',
    companySize: '',
    linkedinUrl: '',
    location: '',
    contactPerson: '',
    priority: 'WARM',
    stage: 'NEW',
    dealValue: undefined,
    expectedClose: '',
    assignedTo: '',
    sourceId: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch team members for assignment
  const { data: teamMembers } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
    enabled: isOpen
  });

  // Fetch lead sources for dropdown
  const { data: leadSources } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api.getLeadSources(),
    enabled: isOpen
  });

  const createLeadMutation = useMutation({
    mutationFn: (data: CreateLeadData) => api.createLead(data),
    onSuccess: (newLead) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      onLeadCreated(newLead);
      onClose();
      resetForm();
      toast({ title: 'Lead created successfully', description: `${newLead?.name || 'New lead'} has been added to the pipeline.` });
    },
    onError: (error: any) => {
      console.error('Failed to create lead:', error);
      // api.ts uses fetch (not axios), so errors are plain Error objects with message
      const errorMessage = error?.message || 'Failed to create lead. Please try again.';
      setErrors({ submit: errorMessage });
      toast({ title: 'Failed to create lead', description: errorMessage, variant: 'destructive' });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      phoneNumber: '',
      industry: '',
      companySize: '',
      linkedinUrl: '',
      location: '',
      contactPerson: '',
      priority: 'WARM',
      stage: 'NEW',
      dealValue: undefined,
      expectedClose: '',
      assignedTo: '',
      sourceId: ''
    });
    setErrors({});
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Lead name is required';
    }
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!formData.sourceId) {
      newErrors.sourceId = 'Lead source is required';
    }

    // Business logic validation
    if (formData.stage === 'NEW' && formData.dealValue) {
      newErrors.dealValue = 'Deal value cannot be set for NEW stage leads';
    }

    if (['PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) && !formData.dealValue) {
      newErrors.dealValue = 'Deal value is required for Proposal stage and beyond';
    }

    if (['NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) && !formData.expectedClose) {
      newErrors.expectedClose = 'Expected close date is required for Negotiation stage and beyond';
    }

    // Deal value must be positive
    if (formData.dealValue !== undefined && formData.dealValue < 0) {
      newErrors.dealValue = 'Deal value cannot be negative';
    }

    // Phone number format validation (at least 10 digits)
    if (formData.phoneNumber && !/^\+?[\d\s-]{10,}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number (at least 10 digits)';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // LinkedIn URL validation
    if (formData.linkedinUrl && !formData.linkedinUrl.includes('linkedin.com')) {
      newErrors.linkedinUrl = 'Please enter a valid LinkedIn URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Strip empty strings to undefined so Zod optional() validation passes.
      // Convert date-only strings (yyyy-mm-dd) to full ISO-8601 datetimes.
      const cleanedData: CreateLeadData = {
        ...formData,
        email: formData.email?.trim() || undefined,
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        industry: formData.industry?.trim() || undefined,
        companySize: formData.companySize?.trim() || undefined,
        linkedinUrl: formData.linkedinUrl?.trim() || undefined,
        location: formData.location?.trim() || undefined,
        contactPerson: formData.contactPerson?.trim() || undefined,
        assignedTo: formData.assignedTo?.trim() || undefined,
        sourceId: formData.sourceId?.trim() || undefined,
        expectedClose: formData.expectedClose
          ? formData.expectedClose.length === 10
            ? new Date(formData.expectedClose + 'T23:59:59.000Z').toISOString()
            : formData.expectedClose
          : undefined,
      };
      createLeadMutation.mutate(cleanedData);
    }
  };

  const handleInputChange = (field: keyof CreateLeadData, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isDealValueVisible = !['NEW', 'CONTACTED', 'DISCUSSION'].includes(formData.stage);
  const isExpectedCloseVisible = ['NEGOTIATION', 'WON', 'LOST'].includes(formData.stage);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Add new lead"
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Card className="p-6 bg-white shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Add New Lead</h2>
                    <p className="text-sm text-gray-600">Create a comprehensive lead profile. <span className="text-red-500">*</span> = required</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  aria-label="Close add lead modal"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Lead Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <User className="h-5 w-5 mr-2 text-blue-600" />
                      Lead Information
                    </h3>

                    {/* Lead Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter lead name"
                        aria-required="true"
                        className={`w-full ${errors.name ? 'border-red-500' : ''}`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="h-4 w-4 inline mr-1 text-gray-500" />
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address"
                        aria-required="true"
                        className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="h-4 w-4 inline mr-1 text-gray-500" />
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        placeholder="Enter phone number"
                        aria-required="true"
                        className={`w-full ${errors.phoneNumber ? 'border-red-500' : ''}`}
                      />
                      {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                    </div>

                    {/* LinkedIn */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Linkedin className="h-4 w-4 inline mr-1 text-gray-500" />
                        LinkedIn URL
                      </label>
                      <Input
                        type="url"
                        value={formData.linkedinUrl}
                        onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                        className={`w-full ${errors.linkedinUrl ? 'border-red-500' : ''}`}
                      />
                      {errors.linkedinUrl && <p className="text-red-500 text-xs mt-1">{errors.linkedinUrl}</p>}
                    </div>

                    {/* Contact Person */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Users className="h-4 w-4 inline mr-1 text-gray-500" />
                        Primary Contact
                      </label>
                      <Input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        placeholder="Enter primary contact person"
                        className="w-full"
                      />
                    </div>

                    {/* Lead Source */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Target className="h-4 w-4 inline mr-1 text-gray-500" />
                        Lead Source <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.sourceId || ''}
                        onChange={(e) => handleInputChange('sourceId', e.target.value || undefined)}
                        aria-label="Lead source"
                        aria-required="true"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.sourceId ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select lead source</option>
                        {leadSources?.map(source => (
                          <option key={source.id} value={source.id}>
                            {source.name}
                          </option>
                        ))}
                      </select>
                      {errors.sourceId && <p className="text-red-500 text-xs mt-1">{errors.sourceId}</p>}
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Building className="h-5 w-5 mr-2 text-green-600" />
                      Company Information
                    </h3>

                    {/* Company Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        placeholder="Enter company name"
                        aria-required="true"
                        className={`w-full ${errors.company ? 'border-red-500' : ''}`}
                      />
                      {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
                    </div>

                    {/* Industry */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select industry</option>
                        {INDUSTRIES.map(industry => (
                          <option key={industry} value={industry}>{industry}</option>
                        ))}
                      </select>
                    </div>

                    {/* Company Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        value={formData.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select company size</option>
                        {COMPANY_SIZES.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-1 text-gray-500" />
                        Location
                      </label>
                      <Input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="Enter location"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Deal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-purple-600" />
                    Deal Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Status
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value as 'HOT' | 'WARM' | 'COLD')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="HOT">🔥 Hot</option>
                        <option value="WARM">🟡 Warm</option>
                        <option value="COLD">🧊 Cold</option>
                      </select>
                    </div>

                    {/* Pipeline Stage */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pipeline Stage
                      </label>
                      <select
                        value={formData.stage}
                        onChange={(e) => handleInputChange('stage', e.target.value as Lead['stage'])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {PIPELINE_STAGES.map(stage => (
                          <option key={stage.value} value={stage.value}>
                            {stage.label} - {stage.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Assigned To */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assigned To
                      </label>
                      <select
                        value={formData.assignedTo}
                        onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">{Array.isArray(teamMembers) && teamMembers.length > 0 ? 'Select team member' : 'No team members available'}</option>
                        {Array.isArray(teamMembers) && teamMembers.length > 0 ? (
                          teamMembers.map(member => (
                            <option key={member.id} value={member.email}>
                              {member.name} ({member.email})
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>No team members available</option>
                        )}
                      </select>
                      {!Array.isArray(teamMembers) || teamMembers.length === 0 ? (
                        <p className="text-xs text-gray-500 mt-1">No team members found. You can assign later.</p>
                      ) : null}
                    </div>
                  </div>

                  {/* Deal Value - Only visible for Proposal+ stages */}
                  {isDealValueVisible && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <TrendingUp className="h-4 w-4 inline mr-1 text-gray-500" />
                        Deal Value (₹) {['PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) && <span className="text-red-500">*</span>}
                      </label>
                      <Input
                        type="number"
                        value={formData.dealValue || ''}
                        onChange={(e) => handleInputChange('dealValue', parseInt(e.target.value) || undefined)}
                        placeholder={formData.stage === 'NEW' ? 'Enter estimated value' : 'Enter deal value'}
                        className={`w-full ${errors.dealValue ? 'border-red-500' : ''}`}
                        min="0"
                      />
                      {errors.dealValue && <p className="text-red-500 text-xs mt-1">{errors.dealValue}</p>}
                      {!formData.dealValue && ['PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].includes(formData.stage) && (
                        <p className="text-xs text-gray-500 mt-1">Deal value is required for this stage</p>
                      )}
                    </div>
                  )}

                  {/* Expected Close Date - Only visible for Negotiation+ stages */}
                  {isExpectedCloseVisible && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 inline mr-1 text-gray-500" />
                        Expected Close Date <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="date"
                        value={formData.expectedClose}
                        onChange={(e) => handleInputChange('expectedClose', e.target.value)}
                        className={`w-full ${errors.expectedClose ? 'border-red-500' : ''}`}
                      />
                      {errors.expectedClose && <p className="text-red-500 text-xs mt-1">{errors.expectedClose}</p>}
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createLeadMutation.isPending}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    {createLeadMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Lead
                      </div>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 